<template>
	<footer class="footer @container py-6 lg:py-4" :class="{ boxed }">
		<div class="wrap flex flex-col-reverse justify-between gap-x-4 gap-y-2 @5xl:flex-row @5xl:items-center">
			<div>
				<div v-if="showCopyright" class="text-secondary text-sm">
					{{ $t("common.misc.footer_copyright") }}
				</div>
			</div>
			<div class="flex flex-wrap items-center gap-x-10 gap-y-2 text-sm">
				<LocaleSwitch v-if="showLocaleSwitch" />
				<a
					v-for="link in footerLinks"
					:key="link.key"
					:href="link.url"
					target="_blank"
					:alt="link.title"
					rel="noopener noreferrer"
					class="text-secondary no-underline hover:underline"
				>
					{{ link.title }}
				</a>

				<a
					class="text-secondary cursor-pointer no-underline hover:underline"
					:alt="t('common.misc.cookies')"
					@click="openCookies"
				>
					{{ t("common.misc.cookies") }}
				</a>

				<n-tooltip v-if="!hideVersion" class="max-w-50 px-1! py-0.5!">
					<template #trigger>
						<code class="text-xs">v{{ appVersion }}</code>
					</template>
					<div class="font-mono text-xs">{{ appCommit }}</div>
				</n-tooltip>
			</div>
		</div>
	</footer>
</template>

<script lang="ts" setup>
import type { I18nString } from "@/lang/config"
import { NTooltip } from "naive-ui"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useCookieBanner } from "@/composables/common/useCookieBanner"
import LocaleSwitch from "./Toolbar/LocaleSwitch.vue"

const { boxed, hideVersion, showCopyright } = defineProps<{
	boxed: boolean
	hideVersion?: boolean
	showCopyright?: boolean
}>()

const appVersion = __APP_VERSION__
const appCommit = __APP_COMMIT__
// TODO-NEXT: MVP2 enable locale switch
const showLocaleSwitch = false

// const year = ref(new Date().getFullYear())
const { t } = useI18n()

const footerLinks = computed(() => {
	const links: Array<{ key: string; title: string; url: string }> = []
	const push = (key: string, title: string, url: string | undefined) => {
		if (url && url.trim() && url !== "#") links.push({ key, title, url: url.trim() })
	}

	push("contacts", t<I18nString>("common.misc.contacts"), import.meta.env.VITE_LINK_CONTACTS)
	push("faq", t<I18nString>("common.misc.faq"), import.meta.env.VITE_LINK_FAQ)
	push("credits", t<I18nString>("common.misc.credits"), import.meta.env.VITE_LINK_CREDITS)
	push("privacy_policy", t<I18nString>("common.misc.privacy_policy"), import.meta.env.VITE_LINK_PRIVACY_POLICY)
	push(
		"terms_and_conditions",
		t<I18nString>("common.misc.terms_and_conditions"),
		import.meta.env.VITE_LINK_TERMS_AND_CONDITIONS
	)

	return links
})

const { open } = useCookieBanner()

function openCookies() {
	open()
}
</script>

<style lang="scss" scoped>
.footer {
	width: 100%;
	max-width: 100%;
	overflow: hidden;
	font-size: 13px;

	.wrap {
		overflow: hidden;
		width: 100%;
		max-width: 100%;
		margin: 0 auto;
		padding: 0 var(--view-padding);
	}

	&.boxed {
		.wrap {
			max-width: var(--boxed-width);
		}
	}
}
</style>
